/**
 * Currency configuration for different regions
 * This maps languages to their preferred currency display while maintaining USD processing
 */

export const CURRENCY_CONFIG = {
  en: {
    currency: 'USD',
    symbol: '$',
    amount: 2.49,
    stripeAmount: 249, // Amount in cents for Stripe
    displayFormat: (amount, symbol) => `${symbol}${amount.toFixed(2)}`
  },
  ja: {
    currency: 'JPY',
    symbol: '¥',
    amount: 500,
    stripeAmount: 249, // Still process in USD cents
    displayFormat: (amount, symbol) => `${symbol}${Math.round(amount)}`
  },
  'zh-TW': {
    currency: 'HKD',
    symbol: 'HK$',
    amount: 22,
    stripeAmount: 2200, // HK$22.00 in cents for Stripe processing
    displayFormat: (amount, symbol) => `${symbol}${amount.toFixed(2)}`
  }
};

/**
 * Get currency configuration for a specific language
 * @param {string} language - Language code
 * @returns {Object} Currency configuration
 */
export const getCurrencyConfig = (language) => {
  return CURRENCY_CONFIG[language] || CURRENCY_CONFIG.en;
};

/**
 * Format price for display based on language
 * @param {string} language - Language code
 * @returns {string} Formatted price string
 */
export const getDisplayPrice = (language) => {
  const config = getCurrencyConfig(language);
  return config.displayFormat(config.amount, config.symbol);
};

/**
 * Get the Stripe amount (always in USD cents) regardless of display currency
 * @param {string} language - Language code (unused but kept for consistency)
 * @returns {number} Amount in USD cents for Stripe
 */
export const getStripeAmount = (language) => {
  // Always return USD amount for Stripe processing
  return CURRENCY_CONFIG.en.stripeAmount;
};

/**
 * Get currency symbol for display
 * @param {string} language - Language code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (language) => {
  const config = getCurrencyConfig(language);
  return config.symbol;
};

/**
 * Get currency code for payment processing
 * @param {string} language - Language code
 * @returns {string} Currency code (usd, jpy, hkd)
 */
export const getCurrencyForLanguage = (language) => {
  if (language === 'ja') return 'jpy';
  if (language === 'zh-TW') return 'hkd';
  return 'usd';
};